import type { QuickFillItem, QuickFillRowMode } from '../../../shared/types';
import type {
  ValidationQuickFillFocus,
  ValidationQuickFillSuggestedCellValue,
} from '../../../shared/types/validationNavigation.types';
import {
  applyAutoFillToQuickFillItem,
  autoFillFromQuickFillItem,
} from '../../../shared/lib/utils/autoFillService';
import { resolveQuickFillItemLabel } from './quickFillLabelNormalization';

export interface QuickFillSuggestedCellUpdate extends ValidationQuickFillSuggestedCellValue {
  rowIndex: number;
}

export interface QuickFillFocusWaitReasonInput {
  effectiveRowMode: QuickFillRowMode;
  expectedRowMode: QuickFillRowMode;
  hiddenFocusLabelCount: number;
  isTableLoading: boolean;
  columnsReadyInData: boolean;
  datasetModeReady: boolean;
  dataRowModes: Array<QuickFillRowMode | string>;
  rowCount: number;
  validationScopeResolvedEmpty: boolean;
}

function stripQuickFillRowDisplaySuffix(value: string): string {
  return value
    .trim()
    .replace(/\s*\(ID:?\s*[A-Za-z0-9_-]+\)\s*$/i, '')
    .replace(/\s*\[\d+\]\s*$/g, '')
    .trim();
}

function normalizeQuickFillRowToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s*:\s*/g, ':')
    .replace(/\s+/g, ' ');
}

function parseQuickFillRowName(value: string): { familyName: string; typeName: string | null } {
  const normalizedValue = stripQuickFillRowDisplaySuffix(value);
  const separatorIndex = normalizedValue.indexOf(':');
  if (separatorIndex < 0) {
    return {
      familyName: normalizedValue.trim(),
      typeName: null,
    };
  }

  return {
    familyName: normalizedValue.slice(0, separatorIndex).trim(),
    typeName: normalizedValue.slice(separatorIndex + 1).trim() || null,
  };
}

function normalizeQuickFillFamilyTypeKey(value: string): string {
  return normalizeQuickFillRowToken(stripQuickFillRowDisplaySuffix(value));
}

function hasExplicitElementScope(focus: Pick<ValidationQuickFillFocus, 'elementIds'>): boolean {
  return focus.elementIds.some((elementId) => elementId.trim() !== '');
}

function buildQuickFillFamilyTypeKey(familyName: string | undefined, typeName: string | undefined): string {
  const normalizedFamilyName = stripQuickFillRowDisplaySuffix(familyName || '').trim();
  const normalizedTypeName = stripQuickFillRowDisplaySuffix(typeName || '').trim();
  if (!normalizedFamilyName) {
    return '';
  }

  return normalizedTypeName ? `${normalizedFamilyName}:${normalizedTypeName}` : normalizedFamilyName;
}

function matchesScopedFamilyTypeKey(candidateKey: string, scopedKey: string): boolean {
  if (!candidateKey || !scopedKey) {
    return false;
  }

  if (scopedKey.includes(':')) {
    return candidateKey === scopedKey;
  }

  return candidateKey === scopedKey || candidateKey.startsWith(`${scopedKey}:`);
}

function addElementIdCandidate(target: Set<string>, value: unknown): void {
  if (typeof value !== 'string') {
    return;
  }

  const trimmed = value.trim();
  if (trimmed) {
    target.add(trimmed);
  }
}

function collectElementIdMatches(data: QuickFillItem[], elementIds: string[]): number[] {
  if (elementIds.length === 0) {
    return [];
  }

  const normalizedElementIds = new Set(elementIds.map((elementId) => elementId.trim()).filter(Boolean));
  return data
    .map((item, index) => ({
      index,
      elementId:
        typeof (item as any).__elementId === 'string' ? (item as any).__elementId.trim() : '',
    }))
    .filter(({ elementId }) => elementId !== '' && normalizedElementIds.has(elementId))
    .map(({ index }) => index)
    .sort((left, right) => left - right);
}

function collectRowScopedElementIdMatches(data: QuickFillItem[], elementIds: string[]): number[] {
  if (elementIds.length === 0) {
    return [];
  }

  const normalizedElementIds = new Set(elementIds.map((elementId) => elementId.trim()).filter(Boolean));
  if (normalizedElementIds.size === 0) {
    return [];
  }

  return data
    .map((item, index) => {
      const rowElementIds = new Set<string>();
      addElementIdCandidate(rowElementIds, (item as any).__elementId);

      const modelElementIds = (item as any).__modelElementIds;
      if (Array.isArray(modelElementIds)) {
        modelElementIds.forEach((elementId) => addElementIdCandidate(rowElementIds, elementId));
      }

      const instanceTargets = (item as any).__instanceTargetElementIdsByField;
      if (instanceTargets && typeof instanceTargets === 'object') {
        Object.values(instanceTargets).forEach((targetIds) => {
          if (Array.isArray(targetIds)) {
            targetIds.forEach((elementId) => addElementIdCandidate(rowElementIds, elementId));
          }
        });
      }

      return { index, rowElementIds };
    })
    .filter(({ rowElementIds }) =>
      Array.from(rowElementIds).some((elementId) => normalizedElementIds.has(elementId))
    )
    .map(({ index }) => index)
    .sort((left, right) => left - right);
}

function collectQuickFillRowElementIds(
  item: QuickFillItem | undefined,
  label?: string
): Set<string> {
  const rowElementIds = new Set<string>();
  if (!item) {
    return rowElementIds;
  }

  addElementIdCandidate(rowElementIds, (item as any).__elementId);

  const modelElementIds = (item as any).__modelElementIds;
  if (Array.isArray(modelElementIds)) {
    modelElementIds.forEach((elementId) => addElementIdCandidate(rowElementIds, elementId));
  }

  const instanceTargets = (item as any).__instanceTargetElementIdsByField;
  if (instanceTargets && typeof instanceTargets === 'object') {
    const targetMap = instanceTargets as Record<string, unknown>;
    const targetIdsForLabel = label ? targetMap[label] : null;
    if (Array.isArray(targetIdsForLabel)) {
      targetIdsForLabel.forEach((elementId) => addElementIdCandidate(rowElementIds, elementId));
    } else {
      Object.values(targetMap).forEach((targetIds) => {
        if (Array.isArray(targetIds)) {
          targetIds.forEach((elementId) => addElementIdCandidate(rowElementIds, elementId));
        }
      });
    }
  }

  return rowElementIds;
}

function collectFamilyTypeKeyMatches(
  data: QuickFillItem[],
  familyTypeKeys: string[]
): number[] {
  const normalizedKeys = Array.from(
    new Set(familyTypeKeys.map((key) => normalizeQuickFillFamilyTypeKey(key)).filter(Boolean))
  );
  if (normalizedKeys.length === 0) {
    return [];
  }

  const familyTypeMap = new Map<string, number[]>();
  data.forEach((item, index) => {
    const rawFamilyTypeKey =
      typeof (item as any).__familyTypeKey === 'string' ? (item as any).__familyTypeKey : '';
    const normalizedFamilyTypeKey = normalizeQuickFillFamilyTypeKey(rawFamilyTypeKey || item?.Name || '');
    if (!normalizedFamilyTypeKey) {
      return;
    }

    const rowIndices = familyTypeMap.get(normalizedFamilyTypeKey) ?? [];
    rowIndices.push(index);
    familyTypeMap.set(normalizedFamilyTypeKey, rowIndices);
  });

  const matchedIndices = new Set<number>();

  normalizedKeys.forEach((normalizedKey) => {
    if (normalizedKey.includes(':')) {
      (familyTypeMap.get(normalizedKey) ?? []).forEach((index) => matchedIndices.add(index));
      return;
    }

    familyTypeMap.forEach((indices, familyTypeKey) => {
      if (familyTypeKey === normalizedKey || familyTypeKey.startsWith(`${normalizedKey}:`)) {
        indices.forEach((index) => matchedIndices.add(index));
      }
    });
  });

  return Array.from(matchedIndices).sort((left, right) => left - right);
}

function canEditQuickFillLabel(item: QuickFillItem | undefined, label: string): boolean {
  const readonlyFlags =
    (((item as any)?.__readonlyFlags as Record<string, boolean> | undefined) || {});
  return readonlyFlags[label] !== true;
}

function hasQuickFillLabelParameter(item: QuickFillItem | undefined, label: string): boolean {
  const hasParamFlags =
    (((item as any)?.__hasParamFlags as Record<string, boolean> | undefined) || {});
  return hasParamFlags[label] !== false;
}

function isEmptyQuickFillLabelValue(item: QuickFillItem | undefined, label: string): boolean {
  const value = item?.[label as keyof QuickFillItem];
  return typeof value !== 'string' || value.trim() === '';
}

function rowMatchesSuggestedCellValue(
  item: QuickFillItem | undefined,
  suggestedCellValue: ValidationQuickFillSuggestedCellValue,
  label = suggestedCellValue.label
): boolean {
  const elementId = suggestedCellValue.elementId.trim();
  if (!elementId) {
    return false;
  }

  return collectQuickFillRowElementIds(item, label).has(elementId);
}

function rowHasSuggestedCellRepair(item: QuickFillItem | undefined, focus: ValidationQuickFillFocus): boolean {
  const suggestedCellValues = focus.suggestedCellValues ?? [];
  if (suggestedCellValues.length === 0) {
    return false;
  }

  return suggestedCellValues.some((suggestedCellValue) => {
    const label = resolveQuickFillItemLabel(item, suggestedCellValue.label);
    if (
      !rowMatchesSuggestedCellValue(item, suggestedCellValue, label) ||
      !canEditQuickFillLabel(item, label) ||
      !hasQuickFillLabelParameter(item, label)
    ) {
      return false;
    }

    return String(item?.[label as keyof QuickFillItem] ?? '') !== suggestedCellValue.value;
  });
}

function rowNeedsQuickFillRepair(item: QuickFillItem | undefined, focus: ValidationQuickFillFocus): boolean {
  if (!item) {
    return false;
  }

  if (rowHasSuggestedCellRepair(item, focus)) {
    return true;
  }

  const targetLabels = Array.from(new Set((focus.labels ?? []).filter(Boolean)));
  if (targetLabels.length === 0) {
    return false;
  }

  const autoFillResult = autoFillFromQuickFillItem(item as unknown as Record<string, unknown>);

  return targetLabels.some((label) => {
    if (!canEditQuickFillLabel(item, label) || !hasQuickFillLabelParameter(item, label)) {
      return false;
    }

    if (focus.autofillLabels.includes(label)) {
      return (
        isEmptyQuickFillLabelValue(item, label) ||
        applyAutoFillToQuickFillItem(item, autoFillResult, [label], focus.autofillMode) !== item
      );
    }

    return isEmptyQuickFillLabelValue(item, label);
  });
}

export function resolveQuickFillFocusMatchIndices(
  data: QuickFillItem[],
  focus: ValidationQuickFillFocus,
  rowMode: QuickFillRowMode
): number[] {
  const explicitElementScope = hasExplicitElementScope(focus);

  if (rowMode === 'instance') {
    const exactElementIdMatches = collectElementIdMatches(data, focus.elementIds);
    if (exactElementIdMatches.length > 0) {
      return exactElementIdMatches;
    }
  }

  const rowScopedElementIdMatches = collectRowScopedElementIdMatches(data, focus.elementIds);
  if (rowScopedElementIdMatches.length > 0) {
    return rowScopedElementIdMatches;
  }

  if (rowMode === 'instance' && explicitElementScope) {
    return [];
  }

  if (rowMode === 'instance') {
    const familyTypeKeyMatches = collectFamilyTypeKeyMatches(data, focus.familyTypeKeys);
    if (familyTypeKeyMatches.length > 0) {
      return familyTypeKeyMatches;
    }
  }

  const normalizedCandidates = Array.from(
    new Set(
      focus.rowCandidates
        .map((candidate) => stripQuickFillRowDisplaySuffix(candidate))
        .filter(Boolean)
    )
  );

  if (normalizedCandidates.length === 0) {
    return [];
  }

  const exactRowMap = new Map<string, number[]>();
  const familyMap = new Map<string, number[]>();

  data.forEach((item, index) => {
    const rowName = item?.Name?.trim();
    if (!rowName) {
      return;
    }

    const normalizedRowName = normalizeQuickFillRowToken(stripQuickFillRowDisplaySuffix(rowName));
    const rowIndices = exactRowMap.get(normalizedRowName) ?? [];
    rowIndices.push(index);
    exactRowMap.set(normalizedRowName, rowIndices);

    const parsedRowName = parseQuickFillRowName(rowName);
    const normalizedFamilyName = normalizeQuickFillRowToken(parsedRowName.familyName);
    const familyRowIndices = familyMap.get(normalizedFamilyName) ?? [];
    familyRowIndices.push(index);
    familyMap.set(normalizedFamilyName, familyRowIndices);
  });

  const matchedIndices = new Set<number>();

  for (const candidate of normalizedCandidates) {
    const normalizedCandidate = normalizeQuickFillRowToken(stripQuickFillRowDisplaySuffix(candidate));
    const exactMatches = exactRowMap.get(normalizedCandidate) ?? [];
    if (exactMatches.length > 0) {
      if (rowMode === 'instance') {
        exactMatches.forEach((index) => matchedIndices.add(index));
        continue;
      }

      if (exactMatches.length === 1) {
        matchedIndices.add(exactMatches[0]);
        continue;
      }

      return [];
    }

    const parsedCandidate = parseQuickFillRowName(candidate);
    if (!parsedCandidate.familyName || parsedCandidate.typeName) {
      continue;
    }

    const familyMatches =
      familyMap.get(normalizeQuickFillRowToken(parsedCandidate.familyName)) ?? [];
    if (familyMatches.length > 0) {
      if (rowMode === 'instance') {
        familyMatches.forEach((index) => matchedIndices.add(index));
        continue;
      }

      if (familyMatches.length === 1) {
        matchedIndices.add(familyMatches[0]);
        continue;
      }

      return [];
    }
  }

  return Array.from(matchedIndices).sort((left, right) => left - right);
}

export function buildQuickFillFocusConsumptionKey(
  focus: Pick<ValidationQuickFillFocus, 'requestId' | 'labels' | 'suggestedCellValues'>
): string {
  return [
    focus.requestId,
    focus.labels.join('|'),
    (focus.suggestedCellValues ?? [])
      .map((suggestedCellValue) =>
        `${suggestedCellValue.elementId}:${suggestedCellValue.label}:${suggestedCellValue.value}`
      )
      .join('|'),
  ].join('::');
}

export function resolveQuickFillFocusWaitReasons({
  effectiveRowMode,
  expectedRowMode,
  hiddenFocusLabelCount,
  isTableLoading,
  columnsReadyInData,
  datasetModeReady,
  dataRowModes,
  rowCount,
  validationScopeResolvedEmpty,
}: QuickFillFocusWaitReasonInput): string[] {
  const waitReasons: string[] = [];

  if (effectiveRowMode !== expectedRowMode) {
    waitReasons.push('row-mode-mismatch');
  }

  if (hiddenFocusLabelCount > 0) {
    waitReasons.push('hidden-columns');
  }

  if (isTableLoading) {
    waitReasons.push('table-loading');
  }

  if (!columnsReadyInData) {
    waitReasons.push('focus-columns-not-ready');
  }

  if (!datasetModeReady && !validationScopeResolvedEmpty) {
    waitReasons.push(`dataset-mode-${dataRowModes.join(',') || 'unknown'}`);
  }

  if (rowCount === 0 && !validationScopeResolvedEmpty) {
    waitReasons.push('no-rows');
  }

  return waitReasons;
}

export function resolveQuickFillRepairRowIndices(
  data: QuickFillItem[],
  matchedRowIndices: number[],
  focus: ValidationQuickFillFocus
): number[] {
  const uniqueRowIndices = Array.from(
    new Set(matchedRowIndices.filter((rowIndex) => Number.isInteger(rowIndex) && rowIndex >= 0))
  ).sort((left, right) => left - right);

  if (uniqueRowIndices.length === 0) {
    return [];
  }

  const changedRowIndices = uniqueRowIndices.filter((rowIndex) =>
    rowNeedsQuickFillRepair(data[rowIndex], focus)
  );

  return changedRowIndices.length > 0 ? changedRowIndices : uniqueRowIndices;
}

export function resolveQuickFillSuggestedCellUpdates(
  data: QuickFillItem[],
  matchedRowIndices: number[],
  focus: ValidationQuickFillFocus
): QuickFillSuggestedCellUpdate[] {
  const suggestedCellValues = focus.suggestedCellValues ?? [];
  if (suggestedCellValues.length === 0) {
    return [];
  }

  const updates = new Map<string, QuickFillSuggestedCellUpdate>();
  const conflictedUpdateKeys = new Set<string>();
  matchedRowIndices.forEach((rowIndex) => {
    const item = data[rowIndex];
    suggestedCellValues.forEach((suggestedCellValue) => {
      const label = resolveQuickFillItemLabel(item, suggestedCellValue.label);
      if (
        !rowMatchesSuggestedCellValue(item, suggestedCellValue, label) ||
        !canEditQuickFillLabel(item, label) ||
        !hasQuickFillLabelParameter(item, label)
      ) {
        return;
      }

      if (String(item?.[label as keyof QuickFillItem] ?? '') === suggestedCellValue.value) {
        return;
      }

      const updateKey = `${rowIndex}:${label}`;
      if (conflictedUpdateKeys.has(updateKey)) {
        return;
      }

      const existingUpdate = updates.get(updateKey);
      if (existingUpdate && existingUpdate.value !== suggestedCellValue.value) {
        updates.delete(updateKey);
        conflictedUpdateKeys.add(updateKey);
        return;
      }

      updates.set(updateKey, {
        ...suggestedCellValue,
        label,
        rowIndex,
      });
    });
  });

  return Array.from(updates.values()).sort((left, right) => {
    if (left.rowIndex !== right.rowIndex) {
      return left.rowIndex - right.rowIndex;
    }

    return left.label.localeCompare(right.label);
  });
}
